import { ComponentType, ReactNode } from 'react'
import { AccountTypeGuard } from '@/components/auth/AccountTypeGuard'
import { FeatureFlagGuard } from '@/components/auth/FeatureFlagGuard'
import { SubscriptionFeatureGuard } from '@/components/auth/SubscriptionFeatureGuard'
import { OrgRouteGuard } from '@/components/organization/OrgRouteGuard'
import { FeatureErrorBoundary } from '@/components/shared'
import { RequireMemberAccess } from '@/components/shared/RequireMemberAccess'
import { Navigate, useLocation, useParams } from 'react-router-dom'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import { AccountType } from '@scrambl/shared'

import { ProtectedLayout } from '../components'
import { AppRouteObject } from '../types'

// Cache route-module imports so repeated visits don't re-trigger the dynamic import
// promise within the same window. We intentionally do not start these imports at
// bootstrap because every desktop popup/new window gets its own JS context, and
// eager route imports substantially increase first paint time.
const eagerCache = new Map<string, Promise<Record<string, unknown>>>()

function eagerImport(
  importFn: () => Promise<Record<string, unknown>>,
): () => Promise<Record<string, unknown>> {
  const key = importFn.toString()
  return () => {
    const cachedImport = eagerCache.get(key)
    if (cachedImport) {
      return cachedImport
    }

    const nextImport = importFn()
    eagerCache.set(key, nextImport)
    return nextImport
  }
}

function LegacyMemberRatesAndCompensationRedirect() {
  const location = useLocation()
  const { orgSlug = '', memberId = '' } = useParams<{
    orgSlug: string
    memberId: string
  }>()
  return (
    <Navigate
      to={`/${orgSlug}/team/people/${memberId}/pay${location.hash || ''}`}
      replace
    />
  )
}

function LegacyMyExpensesRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  const suffix = location.pathname.split('/my-expenses')[1] || ''

  return (
    <Navigate
      to={`/${orgSlug}/my-time/expenses${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

function LegacyMyTasksRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  const suffix = location.pathname.endsWith('/reviews')
    ? '/reviews'
    : location.pathname.endsWith('/settings')
      ? '/settings'
      : '/mine'

  return (
    <Navigate
      to={`/${orgSlug}/tasks${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

function LegacyMyNotesRedirect() {
  const location = useLocation()
  const { orgSlug = '', noteId } = useParams<{
    orgSlug: string
    noteId?: string
  }>()
  const isCollectionsRoute = location.pathname.endsWith('/collections')
  const suffix = noteId ? `/${encodeURIComponent(noteId)}` : ''
  const nextSearch = new URLSearchParams(location.search)
  if (isCollectionsRoute) nextSearch.set('type', 'collections')
  const search = nextSearch.toString()

  return (
    <Navigate
      to={`/${orgSlug}/notes/mine${suffix}${search ? `?${search}` : ''}${location.hash}`}
      replace
    />
  )
}

function LegacyCommercialOpsRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  return (
    <Navigate
      to={`/${orgSlug}/contracts/billing${location.search}${location.hash}`}
      replace
    />
  )
}

// Old per-status invoice tab routes (/invoices/draft etc.) map onto the
// single filter-bar list: /invoices?status=DRAFT. Settings keeps its route.
function LegacyInvoiceTabRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  const segment = location.pathname.split('/').filter(Boolean).pop()
  const statusBySegment: Record<string, string> = {
    draft: 'DRAFT',
    sent: 'SENT',
    paid: 'PAID',
    overdue: 'OVERDUE',
  }
  const status = segment ? statusBySegment[segment] : undefined
  const search = new URLSearchParams(location.search)
  if (status) search.set('status', status)
  const query = search.toString()

  return (
    <Navigate
      to={`/${orgSlug}/invoices${query ? `?${query}` : ''}${location.hash}`}
      replace
    />
  )
}

function LegacyHelpRedirect() {
  const location = useLocation()
  const { categorySlug, helpId } = useParams<{
    categorySlug?: string
    helpId?: string
  }>()

  const suffix = (() => {
    if (helpId) return `/articles/${encodeURIComponent(helpId)}`
    if (categorySlug) return `/categories/${encodeURIComponent(categorySlug)}`
    if (location.pathname.endsWith('/help/search')) return '/search'
    return ''
  })()

  return (
    <Navigate to={`/help${suffix}${location.search}${location.hash}`} replace />
  )
}

/**
 * Helper for inline `lazy:` routes that map import → { Component }.
 * Imports stay lazy, but repeat loads reuse the same promise.
 *
 * Usage: `lazy: eagerLazy(() => import('@/pages/foo'), m => m.FooComponent)`
 */
function eagerLazy<T>(
  importFn: () => Promise<T>,
  map: (m: T) => { Component: ComponentType<unknown> },
): () => Promise<{ Component: ComponentType<unknown> }> {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return () => load().then(m => map(m as T))
}

// Helper to create lazy routes with per-window import promise caching
const lazyLoad = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const Component = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      return { Component }
    },
  }
}

// Helper for member-only lazy routes — redirects external users to /today.
// Keep this limited to personal member surfaces such as My Time and My Expenses.
// Time tracking / expenses are business surfaces (TIME_TRACKING off for
// personal accounts), so this also gates to ORGANIZATION — a personal org owner
// passes the member/permission checks but must not reach these routes.
const memberOnlyLazy = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      return {
        Component: () => (
          <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
            <RequireMemberAccess>
              <PageComponent />
            </RequireMemberAccess>
          </AccountTypeGuard>
        ),
      }
    },
  }
}

// Helper for subscription-guarded lazy routes with per-window import promise caching
const lazyLoadWithGuard = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  featureKey: string,
  namedExport?: string,
  options?: { orgOnly?: boolean },
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      const guarded = (
        <SubscriptionFeatureGuard featureKey={featureKey}>
          <PageComponent />
        </SubscriptionFeatureGuard>
      )
      return {
        Component: () =>
          options?.orgOnly ? (
            <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
              {guarded}
            </AccountTypeGuard>
          ) : (
            guarded
          ),
      }
    },
  }
}

const SUBSCRIPTION_FEATURE_BY_FLAG: Partial<
  Record<FeatureFlagKeyType, string>
> = {
  TICKETS: 'tickets',
  DEALS: 'deals',
  INVOICES: 'advanced_reports',
  CONTRACTS: 'contracts',
  FORMS: 'forms',
  EVENTS: 'events',
  SUPPLIERS: 'suppliers',
}

// Helper for feature-flag-guarded lazy routes with per-window import promise caching
const lazyLoadWithFeatureFlag = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  flagKey: FeatureFlagKeyType,
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      const subscriptionFeature = SUBSCRIPTION_FEATURE_BY_FLAG[flagKey]
      return {
        Component: () => {
          const page = subscriptionFeature ? (
            <SubscriptionFeatureGuard featureKey={subscriptionFeature}>
              <PageComponent />
            </SubscriptionFeatureGuard>
          ) : (
            <PageComponent />
          )

          return <FeatureFlagGuard flagKey={flagKey}>{page}</FeatureFlagGuard>
        },
      }
    },
  }
}

const lazyLoadWithFeatureFlags = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  flagKeys: FeatureFlagKeyType[],
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default

      return {
        Component: () =>
          flagKeys.reduceRight(
            (child, flagKey) => {
              const subscriptionFeature = SUBSCRIPTION_FEATURE_BY_FLAG[flagKey]
              const guardedChild = subscriptionFeature ? (
                <SubscriptionFeatureGuard featureKey={subscriptionFeature}>
                  {child}
                </SubscriptionFeatureGuard>
              ) : (
                child
              )

              return (
                <FeatureFlagGuard flagKey={flagKey}>
                  {guardedChild}
                </FeatureFlagGuard>
              )
            },
            (<PageComponent />) as ReactNode,
          ),
      }
    },
  }
}

const tasksErrorElement = <FeatureErrorBoundary featureName="Tasks" />
const invoicesErrorElement = <FeatureErrorBoundary featureName="Invoices" />
const notesErrorElement = <FeatureErrorBoundary featureName="Notes" />
const documentsErrorElement = <FeatureErrorBoundary featureName="Documents" />
const threadsErrorElement = <FeatureErrorBoundary featureName="Threads" />
const ticketsErrorElement = <FeatureErrorBoundary featureName="Tickets" />
const RedirectInboxToNotes = () => {
  const { noteId, orgSlug } = useParams()

  if (!noteId || !orgSlug) {
    return <Navigate to="../notes" replace />
  }

  return <Navigate to={`/${orgSlug}/notes/${noteId}`} replace />
}

// Redirects legacy /meetings → /notes/meetings (Meetings is a Notes sub-tab)
const RedirectToRecordings = () => {
  const { orgSlug } = useParams()
  return (
    <Navigate
      to={orgSlug ? `/${orgSlug}/notes/recordings` : '../notes/recordings'}
      replace
    />
  )
}

// Redirects /team/planning/schedule/:id → /team/people/:id/schedule (if id), else /team/planner
const PlanningScheduleRedirect = () => {
  const { id } = useParams<{ id?: string }>()
  return id ? (
    <Navigate to={`../team/people/${id}/schedule`} replace />
  ) : (
    <Navigate to="../team/planner" replace />
  )
}

// Redirects legacy /team/planning paths to /team/planner, keeping the query
// string so scope params (projectId, entityId, ...) survive the hop.
const PlannerRedirect = () => {
  const location = useLocation()
  return (
    <Navigate
      to={{ pathname: '../team/planner', search: location.search }}
      replace
    />
  )
}

const LegacyTeamRoleRedirect = () => {
  const { roleId } = useParams<{ roleId?: string }>()
  return roleId ? (
    <Navigate to={`../team/rates/roles/${roleId}`} replace />
  ) : (
    <Navigate to="../team/rates/roles" replace />
  )
}

// Organization routes configuration
export const organizationRoutes: AppRouteObject[] = [
  {
    path: '/:orgSlug',
    element: <ProtectedLayout />,
    children: [
      {
        path: 'help',
        element: <LegacyHelpRedirect />,
        title: 'Help Center',
      },
      {
        path: 'help/search',
        element: <LegacyHelpRedirect />,
        title: 'Search Help',
      },
      {
        path: 'help/categories/:categorySlug',
        element: <LegacyHelpRedirect />,
        title: 'Help Category',
      },
      {
        path: 'help/articles/:helpId',
        element: <LegacyHelpRedirect />,
        title: 'Help Article',
      },
      {
        element: <OrgRouteGuard />,
        children: [
          // Default redirect
          {
            index: true,
            element: <Navigate to="today" replace />,
          },
          // Organization Today
          {
            path: 'today',
            ...lazyLoad(() => import('@/pages/today')),
            title: 'Today',
          },
          // Meal planner (personal spaces; MEAL_PLANNER flag is OFF for
          // business). The router is the third sanctioned personal/work seam
          // crossing — see the eslint no-restricted-imports overrides.
          {
            path: 'meals',
            ...lazyLoadWithFeatureFlag(
              () => import('@/personal/meals/MealPlannerPage'),
              'MEAL_PLANNER',
            ),
            title: 'Meals',
          },
          // Guided Setup — resumable onboarding flow (work accounts, admins)
          {
            path: 'getting-started',
            ...lazyLoad(() => import('@/pages/getting-started')),
            title: 'Getting Started',
          },
          // Search
          {
            path: 'search',
            ...lazyLoad(() => import('@/pages/search')),
            title: 'Search',
          },
          // My Organization (intranet) routes
          {
            path: 'my-organization',
            ...lazyLoad(() => import('@/pages/my-organization/layout')),
            title: 'My Organization',
            children: [
              {
                index: true,
                ...lazyLoad(() => import('@/pages/my-organization')),
                title: 'My Organization',
              },
              {
                path: 'overview/:pageSlug',
                ...lazyLoad(() => import('@/pages/my-organization')),
                title: 'My Organization',
              },
              {
                path: 'org-chart',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/org-chart'),
                  m => ({ Component: m.OrganizationOrgChartRoute }),
                ),
                title: 'Org Chart',
              },
              {
                path: 'projects',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/projects'),
                  m => ({ Component: m.OrganizationProjectsRoute }),
                ),
                title: 'Organization Projects',
              },
              {
                path: 'documents',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/documents'),
                  m => ({ Component: m.OrganizationDocumentsRoute }),
                ),
                title: 'Organization Documents',
              },
              {
                path: 'discussions',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/discussions'),
                  m => ({ Component: m.OrganizationDiscussionsRoute }),
                ),
                title: 'Organization Discussions',
              },
              {
                path: 'notes',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/notes'),
                  m => ({ Component: m.OrganizationNotesRoute }),
                ),
                title: 'Organization Notes',
              },
              {
                path: 'tickets',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/tickets'),
                  m => ({ Component: m.OrganizationTicketsRoute }),
                ),
                title: 'Organization Tickets',
              },
              {
                path: ':pageSlug',
                ...lazyLoad(() => import('@/pages/my-organization/page')),
                title: 'Organization Page',
              },
            ],
          },
          // My Tasks route (personal tasks view) — open to external users so
          // they can see tasks they're assigned to in this host org
          {
            path: 'my-tasks',
            element: <LegacyMyTasksRedirect />,
            title: 'My Tasks',
            errorElement: tasksErrorElement,
          },
          {
            path: 'my-tasks/reviews',
            element: <LegacyMyTasksRedirect />,
            title: 'My Tasks Reviews',
            errorElement: tasksErrorElement,
          },
          {
            path: 'my-tasks/settings',
            element: <LegacyMyTasksRedirect />,
            title: 'My Tasks Settings',
            errorElement: tasksErrorElement,
          },
          // My Notes route
          {
            path: 'my-notes',
            element: <LegacyMyNotesRedirect />,
            title: 'My Notes',
            errorElement: notesErrorElement,
          },
          // Collections tab (same page component; static segment outranks :noteId)
          {
            path: 'my-notes/collections',
            element: <LegacyMyNotesRedirect />,
            title: 'My Notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'my-notes/:noteId',
            element: <LegacyMyNotesRedirect />,
            title: 'Note',
            errorElement: notesErrorElement,
          },
          // My Time route (personal time tracking)
          {
            path: 'my-time',
            ...memberOnlyLazy(() => import('@/pages/my-time')),
            title: 'My Time',
            children: [
              // Canonical paths (Plan 10 consolidation)
              {
                path: 'log',
                lazy: eagerLazy(
                  () => import('@/pages/time/tabs/current-tab'),
                  m => ({ Component: m.CurrentTab }),
                ),
                title: 'Log Time',
              },
              {
                path: 'timesheets',
                lazy: eagerLazy(
                  () => import('@/pages/time/tabs/history'),
                  m => ({ Component: m.HistoryTab }),
                ),
                title: 'My Timesheets',
              },
              // Legacy paths → redirect to canonical (redirects stay live ≥ 2026-10-21)
              {
                path: 'current',
                element: <Navigate to="../log" replace />,
              },
              {
                path: 'history',
                element: <Navigate to="../timesheets" replace />,
              },
              {
                path: 'availability',
                lazy: eagerLazy(
                  () => import('@/pages/workspace/tabs/availability'),
                  m => ({ Component: m.default }),
                ),
                title: 'Availability',
              },
              {
                path: 'time-off',
                lazy: eagerLazy(
                  () => import('@/pages/workspace/tabs/time-off'),
                  m => ({ Component: m.default }),
                ),
                title: 'Time Off',
              },
              {
                path: 'expenses',
                children: [
                  {
                    index: true,
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My Expenses',
                  },
                  {
                    path: 'reimbursements',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My Expense Reimbursements',
                  },
                  {
                    path: 'reimbursements/:expenseId/*',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My Expense Reimbursement Details',
                  },
                  {
                    path: ':expenseId/*',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My Expense Details',
                  },
                ],
              },
              {
                path: 'contractor-invoices',
                lazy: eagerLazy(
                  () => import('@/pages/my-time/tabs/invoices'),
                  m => ({ Component: m.default }),
                ),
                title: 'Contractor Invoices',
              },
              // Legacy invoices path
              {
                path: 'invoices',
                element: <Navigate to="../contractor-invoices" replace />,
              },
            ],
          },
          // Timesheets — consolidated surface for managers/admins
          {
            path: 'timesheets',
            ...lazyLoad(() => import('@/pages/timesheets')),
            title: 'Timesheets',
            children: [
              {
                index: true,
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/approvals'),
                  m => ({ Component: m.ApprovalsTab }),
                ),
                title: 'All Timesheets',
              },
              {
                path: 'reports',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/reports'),
                  m => ({ Component: m.TimesheetsReportsTab }),
                ),
                title: 'Reports',
              },
              {
                path: 'invoices',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/invoices'),
                  m => ({ Component: m.TimesheetsInvoicesTab }),
                ),
                title: 'Invoices',
              },
              {
                path: 'settings',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/settings'),
                  m => ({ Component: m.TimesheetsSettingsTab }),
                ),
                title: 'Settings',
              },
            ],
          },
          // Entities routes
          {
            path: 'entities/workflows',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entity Workflows',
          },
          {
            path: 'entities/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entity Settings',
          },
          {
            path: 'entities/health-triage',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/entities'),
              ['ENTITIES', 'CLIENT_HEALTH'],
            ),
            title: 'Entity Health Triage',
          },
          {
            path: 'entities/:entityId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities/entity-detail'),
              'ENTITIES',
              'EntityDetail',
            ),
            title: 'Entity Details',
          },
          {
            path: 'entities',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entities',
          },
          // Collections routes (requires subscription feature)
          {
            path: 'collections/:slug/settings',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/[slug]'),
              'collections',
            ),
            title: 'Collection Settings',
          },
          {
            path: 'collections/:slug/:entryId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/entry-detail'),
              'collections',
            ),
            title: 'Collection Entry',
          },
          {
            path: 'collections/:slug',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/[slug]'),
              'collections',
            ),
            title: 'Collection Details',
          },
          {
            path: 'collections',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections'),
              'collections',
            ),
            title: 'Collections',
          },
          // Projects routes
          {
            path: 'projects/:projectId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects/project-detail'),
              'PROJECTS',
              'ProjectDetail',
            ),
            title: 'Project Details',
          },
          {
            path: 'projects/roadmap',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Roadmap',
          },
          {
            path: 'projects/templates',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project Templates',
          },
          {
            path: 'projects/reports/velocity',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project Velocity Report',
          },
          {
            path: 'projects/reports',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project Reports',
          },
          {
            path: 'projects/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project Settings',
          },
          {
            path: 'projects',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Projects',
          },
          // Tasks routes (All Tasks module - My Tasks is now at /my-tasks)
          {
            path: 'tasks/mine',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'My Tasks',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/mine/settings',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'My Work Settings',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/reviews',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'Task Reviews',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/settings',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'Task Settings',
            errorElement: tasksErrorElement,
          },
          {
            // Registered before tasks/:taskIdentifier so "workstream" is not
            // captured as a task identifier. Workstream is a tab inside the
            // Tasks module but keeps its own WORKSTREAM flag gating.
            path: 'tasks/workstream/:workspaceId?',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tasks'),
              'WORKSTREAM',
            ),
            title: 'Workstream',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/:taskIdentifier',
            lazy: eagerLazy(
              () => import('@/pages/tasks/task-detail'),
              m => ({ Component: m.TaskDetail }),
            ),
            title: 'Task Details',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/tasks'), 'TASKS'),
            title: 'All Tasks',
            errorElement: tasksErrorElement,
          },
          {
            path: 'focus',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/focus'),
              'FOCUS_MODE',
            ),
            title: 'Focus',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tickets/:ticketIdentifier',
            lazy: eagerLazy(
              () => import('@/pages/tickets/ticket-detail'),
              m => ({ Component: m.TicketDetail }),
            ),
            title: 'Ticket Details',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Tickets',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket Settings',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/roadmap',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket Roadmap',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/insights',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket Insights',
            errorElement: ticketsErrorElement,
          },
          // Calendar routes
          {
            path: 'calendar',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar',
          },
          {
            path: 'calendar/month',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Month',
          },
          {
            path: 'calendar/week',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Week',
          },
          {
            path: 'calendar/day',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Day',
          },
          {
            path: 'calendar/agenda',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Agenda',
          },
          {
            path: 'calendar/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar Settings',
          },
          // Timesheet approval detail page
          {
            path: 'timesheets/approvals/:userId',
            ...lazyLoad(
              () => import('@/pages/timesheets/timesheet-detail'),
              'ApprovalDetail',
            ),
            title: 'Timesheet Approval Detail',
          },
          // Legacy /time/* redirects (grace period: ≥ 2026-10-21)
          {
            path: 'time/log',
            element: <Navigate to="../my-time/log" replace />,
          },
          {
            path: 'time/approvals',
            element: <Navigate to="../timesheets" replace />,
          },
          {
            path: 'time/history',
            element: <Navigate to="../my-time/timesheets" replace />,
          },
          {
            path: 'time/reports',
            element: <Navigate to="../timesheets/reports" replace />,
          },
          {
            path: 'time',
            element: <Navigate to="../my-time" replace />,
          },
          {
            path: 'time/*',
            element: <Navigate to="../my-time" replace />,
          },
          // Sales routes
          //
          // Path C: /leads URLs redirect to the unified /contacts view. A lead
          // id is now just a contact id, so /leads/:id is a straight passthrough
          // to /contacts/:id — keeping email / Slack / bookmark deep links valid.
          {
            path: 'leads/settings',
            ...lazyLoad(
              () => import('@/pages/contacts/lead-redirects'),
              'ContactSettingsRedirect',
            ),
            title: 'Contacts',
          },
          {
            path: 'leads/:leadId/*',
            ...lazyLoad(
              () => import('@/pages/contacts/lead-redirects'),
              'ContactDetailRedirect',
            ),
            title: 'Contact Redirect',
          },
          {
            path: 'leads',
            ...lazyLoad(
              () => import('@/pages/contacts/lead-redirects'),
              'ContactsListRedirect',
            ),
            title: 'Contacts',
          },
          // Contacts routes
          {
            path: 'contacts/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts'),
              'CONTACTS',
            ),
            title: 'Contact Settings',
          },
          {
            path: 'contacts/data-quality',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/contacts'),
              ['CONTACTS', 'ENRICHMENT'],
            ),
            title: 'Data quality',
          },
          {
            path: 'contacts/:contactId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts/contact-detail'),
              'CONTACTS',
              'ContactDetail',
            ),
            title: 'Contact Details',
          },
          {
            path: 'contacts',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts'),
              'CONTACTS',
            ),
            title: 'Contacts',
          },
          {
            path: 'deals/settings',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deal Settings',
          },
          {
            path: 'deals/command-center',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/deals'),
              ['DEALS', 'CLIENT_HEALTH'],
            ),
            title: 'Revenue Command Center',
          },
          {
            path: 'deals/forecast',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deal Forecast',
          },
          {
            path: 'deals/:dealId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/deals/deal-detail'),
              'DEALS',
              'DealDetail',
            ),
            title: 'Deal Details',
          },
          {
            path: 'deals',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deals',
          },
          // Marketing routes
          {
            path: 'marketing',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing'),
              'MARKETING',
            ),
            title: 'Marketing',
          },
          {
            path: 'marketing/campaigns/:campaignId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing/campaign-detail'),
              'MARKETING',
              'CampaignDetail',
            ),
            title: 'Campaign',
          },
          {
            path: 'marketing/journeys/:journeyId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing/journey-builder'),
              'MARKETING',
              'JourneyBuilder',
            ),
            title: 'Journey',
          },
          {
            path: 'scheduling',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/scheduling'),
              'SCHEDULING',
            ),
            title: 'Scheduling',
          },
          {
            path: 'communications/sequences/:sequenceId',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/communications/sequence-detail'),
              ['CALLING', 'SEQUENCES'],
              'SequenceDetail',
            ),
            title: 'Sequence',
          },
          {
            path: 'communications',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/communications'),
              'CALLING',
            ),
            title: 'Communications',
          },
          // Quotes routes
          {
            path: 'quotes/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes'),
              'QUOTES',
            ),
            title: 'Quote Settings',
          },
          {
            path: 'quotes/:quoteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes/quote-detail'),
              'QUOTES',
              'QuoteDetail',
            ),
            title: 'Quote',
          },
          {
            path: 'quotes',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes'),
              'QUOTES',
            ),
            title: 'Quotes',
          },
          // Products (price book + stock) — its own module, used by quotes +
          // invoices; also hosts the absorbed Inventory tabs under /products/*
          {
            path: 'products/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/products'),
              'PRODUCTS',
            ),
            title: 'Products',
          },
          // Knowledge base routes. Tabs are path segments; the module page
          // resolves the active tab from the pathname and redirects legacy
          // `?tab=` links.
          {
            path: 'knowledge-base',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge Base',
          },
          {
            path: 'knowledge-base/content',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge Base',
          },
          {
            path: 'knowledge-base/templates',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge Base',
          },
          {
            path: 'knowledge-base/recordings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge Base',
          },
          {
            path: 'knowledge-base/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge Base Settings',
          },
          {
            // Static `recordings` segment registered before the param routes
            // below so it can never be captured as an :articleId.
            path: 'knowledge-base/recordings/:id',
            ...memberOnlyLazy(
              () => import('@/pages/knowledge-base/recordings/[id]'),
            ),
            title: 'SOP Recording',
          },
          {
            path: 'knowledge-base/read/:articleId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base/article-reader'),
              'KNOWLEDGE_BASE',
              'KbArticleReader',
            ),
            title: 'Knowledge Base article',
          },
          {
            path: 'knowledge-base/articles/:articleId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base/article-editor'),
              'KNOWLEDGE_BASE',
              'KbArticleEditor',
            ),
            title: 'Article',
          },
          {
            path: 'live-chat',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/live-chat'),
              'LIVE_CHAT',
            ),
            title: 'Live chat',
          },
          // Billing routes
          {
            path: 'invoices',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices'),
              'INVOICES',
            ),
            title: 'Invoices',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices'),
              'INVOICES',
            ),
            title: 'Invoice Settings',
            errorElement: invoicesErrorElement,
          },
          // Legacy per-status tab routes redirect into the filter bar
          {
            path: 'invoices/all',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/draft',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/sent',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/paid',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/overdue',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/create',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/editor/invoice-editor'),
              'INVOICES',
            ),
            title: 'New Invoice',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/:id/edit',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/editor/invoice-editor'),
              'INVOICES',
            ),
            title: 'Edit Invoice',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/:id',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/invoice-detail'),
              'INVOICES',
            ),
            title: 'Invoice Details',
            errorElement: invoicesErrorElement,
          },
          // Legacy: My Expenses moved under My Time
          {
            path: 'my-expenses/*',
            element: <LegacyMyExpensesRedirect />,
          },
          {
            path: 'expenses',
            children: [
              {
                index: true,
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expenses',
              },
              {
                path: 'inbox',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Inbox',
              },
              {
                path: 'all',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expenses',
              },
              {
                path: 'reimbursements',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Reimbursements',
              },
              {
                path: 'settings',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Settings',
              },
              {
                // Legacy /expenses/all/settings deep links → /expenses/settings
                path: 'all/settings',
                element: <Navigate to="../settings" replace />,
              },
              {
                path: 'inbox/:expenseId/*',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Inbox Detail',
              },
              {
                path: 'all/:expenseId/*',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Detail',
              },
              {
                path: 'reimbursements/:expenseId/*',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Reimbursement Detail',
              },
              {
                path: ':expenseId/*',
                ...lazyLoad(() => import('@/pages/expenses')),
                title: 'Expense Details',
              },
            ],
          },
          {
            path: 'contracts/retainers',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/retainer-dashboard'),
              'CONTRACTS',
              'RetainerDashboard',
            ),
            title: 'Retainer Dashboard',
          },
          {
            path: 'contracts/billing',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Billing',
          },
          {
            path: 'contracts/service-retainers',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Service Retainers',
          },
          {
            path: 'contracts/contract-billing',
            element: <LegacyCommercialOpsRedirect />,
            title: 'Billing',
          },
          {
            path: 'contracts/commercial-ops',
            element: <LegacyCommercialOpsRedirect />,
            title: 'Billing',
          },
          {
            path: 'contracts/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Contract Settings',
          },
          {
            path: 'contracts/:contractId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/contract-detail'),
              'CONTRACTS',
              'ContractDetail',
            ),
            title: 'Contract Details',
          },
          {
            path: 'contracts',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Contracts',
          },
          // Forms routes
          {
            path: 'forms/:formId/edit',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/edit'),
              'FORMS',
            ),
            title: 'Edit Form',
          },
          {
            path: 'forms/:formId/submissions',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/submissions'),
              'FORMS',
            ),
            title: 'Form Submissions',
          },
          {
            path: 'forms/:formId/analytics',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/analytics'),
              'FORMS',
            ),
            title: 'Form Analytics',
          },
          {
            path: 'forms/:formId/branding',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/branding'),
              'FORMS',
            ),
            title: 'Form Branding',
          },
          {
            path: 'forms/:formId/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/settings'),
              'FORMS',
            ),
            title: 'Form Settings',
          },
          {
            // Forms has no module-level settings page. This path used to
            // render the list silently and was advertised in omni-search, so
            // redirect rather than 404 any lingering bookmarks.
            path: 'forms/settings',
            element: <Navigate to="../forms" replace />,
          },
          {
            path: 'forms',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/forms'), 'FORMS'),
            title: 'Forms',
          },
          // Workflows (requires subscription feature)
          {
            path: 'workflows',
            ...lazyLoadWithGuard(
              () => import('@/pages/workflows/visual'),
              'workflows',
            ),
            title: 'Workflows',
          },
          // Analytics routes (requires subscription feature)
          {
            path: 'analytics',
            ...lazyLoadWithGuard(
              () => import('@/pages/analytics'),
              'analytics',
            ),
            title: 'Analytics',
          },
          {
            path: 'analytics/reports',
            ...lazyLoadWithGuard(
              () => import('@/pages/analytics'),
              'analytics',
            ),
            title: 'Reports',
          },
          {
            path: 'analytics/alerts',
            ...lazyLoadWithGuard(
              () => import('@/pages/analytics'),
              'analytics',
            ),
            title: 'Alerts',
          },
          {
            path: 'analytics/dashboards/:id',
            ...lazyLoadWithGuard(
              () => import('@/pages/analytics/DashboardBuilder'),
              'analytics',
            ),
            title: 'Dashboard',
          },
          {
            path: 'analytics/reports/:id',
            ...lazyLoadWithGuard(
              () => import('@/pages/analytics/ReportBuilder'),
              'analytics',
            ),
            title: 'Report',
          },
          // Community routes (requires subscription feature)
          {
            path: 'threads',
            ...lazyLoadWithGuard(
              () => import('@/pages/discussions/page'),
              'discussions',
            ),
            title: 'Threads',
            errorElement: threadsErrorElement,
          },
          // Redirect old discussions path to threads
          {
            path: 'discussions',
            element: <Navigate to="../threads" replace />,
          },
          {
            // Notes has no settings page, but this static route must outrank
            // `notes/:noteId` below — without it the URL loads note detail
            // with noteId="settings", which only reaches the list after a
            // failed fetch.
            path: 'notes/settings',
            element: <Navigate to="../notes" replace />,
          },
          {
            path: 'notes/mine/collections',
            element: <LegacyMyNotesRedirect />,
            title: 'My Note Collections',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/mine/:noteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/[noteId]'),
              'NOTES',
            ),
            title: 'Note',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/mine',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/my-notes'),
              'NOTES',
            ),
            title: 'My Notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/templates',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/templates'),
              'NOTES',
            ),
            title: 'Note Templates',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/templates/:templateId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/template-editor'),
              'NOTES',
            ),
            title: 'Edit Template',
            errorElement: notesErrorElement,
          },
          {
            // Must precede notes/:noteId so the static segment wins.
            path: 'notes/sop-recordings/:id',
            ...memberOnlyLazy(() => import('@/pages/notes/sop-recordings/[id]')),
            title: 'SOP Recording',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/:noteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/[noteId]'),
              'NOTES',
            ),
            title: 'Note',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/notes'), 'NOTES'),
            title: 'Notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/recordings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/recordings'),
              'MEETING_NOTES',
            ),
            title: 'Recordings',
            errorElement: notesErrorElement,
          },
          {
            // Legacy URL from when this tab was called "Meetings"
            path: 'notes/meetings',
            element: <RedirectToRecordings />,
          },
          {
            // Legacy URL from when Meetings was a top-level nav item
            path: 'meetings',
            element: <RedirectToRecordings />,
          },
          {
            path: 'documents/:documentId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/documents/[documentId]'),
              'DOCUMENTS',
            ),
            title: 'Document',
            errorElement: documentsErrorElement,
          },
          {
            path: 'documents',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/documents'),
              'DOCUMENTS',
            ),
            title: 'Documents',
            errorElement: documentsErrorElement,
          },
          // Redirect old inbox routes to notes
          {
            path: 'inbox/:noteId',
            element: <RedirectInboxToNotes />,
          },
          {
            path: 'inbox',
            element: <Navigate to="../notes" replace />,
          },
          {
            path: 'events/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events'),
              'EVENTS',
            ),
            title: 'Event Settings',
          },
          {
            path: 'events/:eventId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events/event-detail'),
              'EVENTS',
              'EventDetail',
            ),
            title: 'Event Details',
          },
          {
            path: 'events',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events'),
              'EVENTS',
            ),
            title: 'Events',
          },

          // Notifications route
          {
            path: 'notifications',
            ...lazyLoad(() => import('@/pages/notifications')),
            title: 'Notifications',
          },

          // Legacy My Profile routes - redirect to user-settings
          {
            path: 'me',
            element: <Navigate to="../user-settings#profile" replace />,
            title: 'My Profile',
          },
          {
            path: 'me/skills',
            element: <Navigate to="../user-settings#skills" replace />,
            title: 'My Skills',
          },
          // Legacy redirects - availability and time-off moved to my-time
          {
            path: 'me/availability',
            element: <Navigate to="../my-time/availability" replace />,
          },
          {
            path: 'me/time-off',
            element: <Navigate to="../my-time/time-off" replace />,
          },

          // User Profile & Settings route (combined profile, skills, and preferences)
          {
            path: 'user-settings',
            ...lazyLoad(() => import('@/pages/organization-account')),
            title: 'My Profile',
          },

          // Settings routes
          {
            path: 'settings',
            ...lazyLoad(() => import('@/pages/settings-hub')),
            title: 'Settings',
          },
          {
            path: 'settings/integrations',
            element: <Navigate to="../user-settings#integrations" replace />,
            title: 'Integrations',
          },
          {
            path: 'settings/agents',
            ...lazyLoad(() => import('@/pages/settings/agents')),
            title: 'Agents',
          },
          {
            path: 'members',
            element: <div>Organization Members</div>,
            title: 'Members',
          },
          // Team routes (requires subscription feature)
          // New primary routes
          {
            path: 'team',
            ...lazyLoadWithGuard(() => import('@/pages/team'), 'team'),
            title: 'Team',
          },
          {
            path: 'team/people',
            ...lazyLoadWithGuard(() => import('@/pages/team'), 'team'),
            title: 'Team People',
          },
          {
            path: 'team/departments',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Departments',
          },
          {
            path: 'team/planner',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Planner',
          },
          {
            // Team-IA Plan 05: Allocations folded into Planner as the List view.
            path: 'team/planner/list',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Planner',
          },
          {
            path: 'team/demand',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Demand',
          },
          {
            path: 'team/demand/:demandId',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Demand Request',
          },
          {
            // Kept for the /team/insights -> /team/utilization redirect.
            path: 'team/insights',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Insights',
          },
          {
            path: 'team/financials',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials',
          },
          {
            path: 'team/financials/overview',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials Overview',
          },
          {
            path: 'team/financials/performance',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials Performance',
          },
          {
            path: 'team/financials/forecast',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials Forecast',
          },
          {
            path: 'team/financials/scenarios',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials Scenarios',
          },
          {
            path: 'team/financials/scenarios/:scenarioId',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Financials Scenario',
          },
          {
            path: 'team/forecast',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Forecast',
          },
          {
            path: 'team/scenarios',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Scenarios',
          },
          {
            path: 'team/scenarios/:scenarioId',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Scenario',
          },
          {
            path: 'team/rates',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates',
          },
          {
            path: 'team/rates/overview',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates Overview',
          },
          {
            path: 'team/rates/rates',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates',
          },
          {
            path: 'team/rates/rate-cards',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rate Cards',
          },
          {
            path: 'team/rates/roles',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates Roles',
          },
          {
            path: 'team/rates/roles/:roleId',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates Role Detail',
          },
          {
            path: 'team/earnings',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Earnings',
          },
          {
            path: 'team/earnings/earnings',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Earnings',
          },
          {
            path: 'team/earnings/invoices',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Contractor Invoices',
          },
          {
            path: 'team/settings',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Settings',
          },
          // Member detail redirect: old combined rates/compensation tab → Rates
          {
            path: 'team/people/:memberId/rates-and-compensation',
            element: <LegacyMemberRatesAndCompensationRedirect />,
          },
          // Person profile routes (new structure under /team/people/:memberId)
          {
            path: 'team/people/:memberId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/team/member-detail'),
              'team',
              'MemberDetail',
            ),
            title: 'Member Details',
          },
          // Legacy redirects - old team tab routes to new equivalents
          {
            path: 'team/planning',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/planning/allocations',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/planning/schedule/:id',
            element: <PlanningScheduleRedirect />,
          },
          {
            path: 'team/planning/schedule',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/schedule',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/allocations',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Allocations',
          },
          {
            // Team-IA Plan 05: Utilization is the renamed Insights tab; it now
            // loads the team page directly (the old insights redirect is gone).
            path: 'team/utilization',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Utilization',
          },
          {
            path: 'team/requests',
            element: <Navigate to="../team/demand" replace />,
          },
          {
            path: 'team/demand-requests',
            element: <Navigate to="../team/demand" replace />,
          },
          {
            path: 'team/compensation',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team Rates',
          },
          {
            path: 'team/roles',
            element: <LegacyTeamRoleRedirect />,
          },
          {
            path: 'team/roles/:roleId',
            element: <LegacyTeamRoleRedirect />,
          },
          {
            path: 'team/skills',
            element: <Navigate to="../team/people" replace />,
          },
          // Member detail redirects: planning + performance → workload (Plan 7b-2)
          {
            path: 'team/people/:memberId/planning',
            element: <Navigate to="workload" replace />,
          },
          {
            path: 'team/people/:memberId/performance',
            element: <Navigate to="workload" replace />,
          },
          // Legacy redirect - old member detail routes (/:memberId/* at team level)
          {
            path: 'team/:memberId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/team/member-detail-redirect'),
              'team',
              'MemberDetailRedirect',
              { orgOnly: true },
            ),
            title: 'Member Details',
          },
          // Legacy redirect - redirect old resources path to team
          {
            path: 'resources',
            element: <Navigate to="../team" replace />,
          },
          {
            path: 'resources/*',
            element: <Navigate to="../team" replace />,
          },
          // Inventory routes — the module UI moved into Products; legacy URLs
          // redirect to their /products equivalents (item detail stays here)
          {
            path: 'inventory/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/inventory/redirect'),
              'INVENTORY',
            ),
            title: 'Inventory',
          },
          {
            path: 'inventory/item/:itemId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/inventory/item-detail'),
              'INVENTORY',
              'InventoryItemDetail',
            ),
            title: 'Inventory Item Details',
          },
          // Suppliers routes
          {
            path: 'suppliers/:supplierId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers/supplier-detail'),
              'SUPPLIERS',
            ),
            title: 'Supplier Details',
          },
          {
            path: 'suppliers',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Suppliers',
          },
          {
            path: 'suppliers/purchase-orders',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Purchase Orders',
          },
          {
            path: 'suppliers/performance',
            ...lazyLoadWithGuard(
              () => import('@/pages/suppliers'),
              'suppliers',
            ),
            title: 'Supplier Performance',
          },
          {
            path: 'suppliers/analytics',
            ...lazyLoadWithGuard(
              () => import('@/pages/suppliers'),
              'suppliers',
            ),
            title: 'Supplier Analytics',
          },
          {
            path: 'suppliers/settings',
            ...lazyLoadWithGuard(
              () => import('@/pages/suppliers'),
              'suppliers',
            ),
            title: 'Supplier Settings',
          },
          // Legacy redirect - redirect old subscription path to admin subscription
          {
            path: 'subscription',
            element: <Navigate to="admin/subscription" replace />,
          },
          {
            path: 'subscription/*',
            element: <Navigate to="admin/subscription" replace />,
          },
        ],
      },
    ],
  },
]
